import { createAuth0Client } from "@auth0/auth0-spa-js";

let _auth0Client;

export const navigationGuard = async (to, from, next) => {
  // Allow routes that don't require authentication
  if (to.matched.some(record => record.meta.noAuth)) {
    next();
    return;
  }

  // Wait for client to be initialized
  if (!_auth0Client) {
    console.warn("Auth0 client not initialized yet, redirecting to login");
    next({ path: "/" });
    return;
  }

  try {
    const isAuthenticated = await _auth0Client.isAuthenticated();
    if (isAuthenticated) {
      next();
    } else {
      next({ path: "/" });
    }
  } catch (error) {
    console.error("Error in navigation guard:", error);
    next({ path: "/" });
  }
};

export const getCurrentUser = async () => {
  if (!_auth0Client) {
    console.warn("Auth0 client not initialized");
    return null;
  }

  try {
    if (await _auth0Client.isAuthenticated()) {
      return await _auth0Client.getUser();
    }
  } catch (error) {
    console.error("Error getting current user:", error);
  }
  return null;
};

function install(
  Vue,
  {
    domain,
    clientId,
    redirectUri,
    audience,
    scope,
    cacheLocation,
    logoutRedirectUri
  }
) {
  const auth = {};

  auth.init = async () => {
    const authParams = {
      redirect_uri: redirectUri,
      scope: scope || "openid profile email"
    };

    // Only add audience if it's provided
    if (audience) {
      authParams.audience = audience;
    }

    _auth0Client = await createAuth0Client({
      domain,
      clientId,
      authorizationParams: authParams,
      cacheLocation: cacheLocation || "memory"
    });
  };

  auth.signIn = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    await _auth0Client.loginWithPopup({
      authorizationParams: {
        redirect_uri: redirectUri
      }
    });
    const user = await _auth0Client.getUser();
    const isAuthenticated = await _auth0Client.isAuthenticated();
    return { user, isAuthenticated };
  };

  auth.signOut = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    await _auth0Client.logout({
      logoutParams: {
        returnTo: logoutRedirectUri
      }
    });
  };

  auth.handleLoginRedirect = async () => {
    if (!_auth0Client) {
      console.warn("Auth0 client not initialized, cannot handle redirect");
      return;
    }
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      try {
        await _auth0Client.handleRedirectCallback();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        console.error("login redirection failed cause", e);
      }
    }
  };

  auth.isAuthenticated = async () => {
    if (!_auth0Client) {
      return false;
    }
    return await _auth0Client.isAuthenticated();
  };

  auth.getUser = async () => {
    if (!_auth0Client) {
      return null;
    }
    if (await _auth0Client.isAuthenticated()) {
      return await _auth0Client.getUser();
    } else {
      return null;
    }
  };

  auth.getTokens = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    try {
      const accessToken = await _auth0Client.getTokenSilently();
      const idToken = await _auth0Client.getIdTokenClaims();
      return {
        idToken: idToken?.__raw,
        accessToken
      };
    } catch (error) {
      console.error("Failed to get tokens, likely expired:", error);
      // Clear auth state and redirect to login
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/login";
      throw error;
    }
  };

  auth.handleSignInWithRedirect = async () => {
    if (!_auth0Client) {
      console.warn("Auth0 client not initialized");
      return;
    }
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      try {
        await _auth0Client.handleRedirectCallback();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        console.error("handleRedirectCallback failed cause", e);
      }
    } else if (!(await _auth0Client.isAuthenticated())) {
      await _auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: redirectUri
        }
      });
    }
  };

  Vue.prototype.$auth = auth;

  console.log("init-auth0-auth");
}

export default { install };
